import { store } from "~/store";
import { VITE_OLD_ESHOP } from "@/config/constants.js";
import type { AxiosError } from "axios";
import type { ErrorItem } from "@/interfaces/ErrorItem";
import type IJsonApiErrorResponse from "~/interfaces/IJsonApiErrorResponse";

interface Translate {
  name: string;
}

const scriptPromises = new Map<string, Promise<string>>();

export function getFinalEshopUrl(path: string) {
  return VITE_OLD_ESHOP + path;
}

export function getTranslate(section: string, componentName: string) {
  const translations = store.getters["translations/getSections"];

  if (translations[section]) {
    const translation = translations[section].find(
      (component: Translate) => component.name == componentName,
    );

    if (translation) {
      return translation;
    }
  }
  return null;
}

export function getTitleTranslate(section: string, componentName: string) {
  const translate = getTranslate(section, componentName);
  if (translate) {
    return translate.title;
  }
  return "";
}

export function getTitleTranslateWithOptions(
  section: string,
  componentName: string,
  options?: unknown,
) {
  const translate = getTranslate(section, componentName);
  if (translate) {
    let finalTranslate = translate.title;
    if (options && typeof options === "object") {
      Object.entries(options).forEach(([key, value]) => {
        if (typeof value === "string" || typeof value === "number") {
          finalTranslate = finalTranslate.replace(`${key}`, `${value}`);
        }
      });
    }
    return finalTranslate;
  }
  return "";
}

export function getLinkSelfTranslate(section: string, componentName: string) {
  const translate = getTranslate(section, componentName);
  let linkSelf = "/";
  if (translate && translate.links && translate.links.self) {
    linkSelf = translate.links.self;
  }
  return linkSelf;
}

export function getEshopLinkTranslate(section: string, componentName: string) {
  const linkSelf = getLinkSelfTranslate(section, componentName);
  return getFinalEshopUrl(linkSelf);
}

export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function errorsToString(reason: AxiosError): string {
  let text = "";
  if (reason.response && reason.response.data) {
    const data: { errors?: ErrorItem[]; message?: unknown } =
      reason.response.data;
    if (data.errors) {
      const list = data.errors
        .map((error: ErrorItem) => error.detail)
        .join("</li><li>");
      text = `<ul><li>${list}</li></ul>`;
    } else if (data.message && typeof data?.message == "string") {
      text = data.message;
    }
  }
  return text != "" ? text : "Ocurrio un error inesperado";
}

export function loadScript(src: string) {
  if (scriptPromises.has(src)) {
    return scriptPromises.get(src)!;
  }

  const promise = new Promise<string>((resolve, reject) => {
    const exists = document.querySelector<HTMLScriptElement>(
      `script[src="${src}"]`,
    );
    if (exists?.dataset.loaded === "true") {
      resolve(src);
      return;
    }

    const script = exists || document.createElement("script");
    const cleanup = () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
    const handleLoad = () => {
      script.dataset.loaded = "true";
      cleanup();
      resolve(src);
    };
    const handleError = () => {
      cleanup();
      scriptPromises.delete(src);
      reject(new Error(`Error al cargar el script: ${src}`));
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    if (!exists) {
      script.src = src;
      document.head.appendChild(script);
    }
  });

  scriptPromises.set(src, promise);

  return promise;
}

export async function parseError(err: unknown): Promise<string | undefined> {
  if (!err) {
    return undefined;
  }
  console.error(err);
  if ((err as { data?: unknown })?.data) {
    try {
      const json = (err as { data?: unknown }).data as IJsonApiErrorResponse;
      if (json && Array.isArray(json.errors)) {
        return json.errors.map((err) => err.detail).join("\n");
      }
      return json as unknown as string;
    } catch (err2) {
      console.log(err2);
    }
  } else if (err instanceof Error) {
    return err.message;
  } else if (err instanceof Response) {
    try {
      const text = await err.text();
      const json = JSON.parse(text) as IJsonApiErrorResponse;
      if (json && Array.isArray(json.errors)) {
        return json.errors.map((err) => err.detail).join("\n");
      }
      return text;
    } catch (err2) {
      console.log(err2);
      return "Error inesperado - " + err.statusText;
    }
  } else if (typeof err == "string") {
    return err;
  }
  return "Error desconocido - " + err;
}

export function toSlug(text: string) {
  return text
    .toString() // Asegura que sea string
    .normalize("NFD") // Separa los acentos de las letras
    .replace(/[\u0300-\u036f]/g, "") // Elimina los acentos
    .toLowerCase() // Convierte a minúsculas
    .trim() // Elimina espacios al inicio/final
    .replace(/[^a-z0-9\s-]/g, "") // Elimina caracteres no deseados
    .replace(/\s+/g, "-") // Reemplaza espacios por guiones
    .replace(/-+/g, "-"); // Reemplaza múltiples guiones por uno
}
